import { useRouteError, useNavigate } from 'react-router-dom';
import { RefreshCw, Home, WifiOff, FileWarning, ServerCrash, ChevronDown, Copy, Check } from 'lucide-react';
import { logger } from 'src/lib/logger';
import { Button } from './ui/Button';
import { useState } from 'react';

type ErrorType = 'network' | 'chunk' | 'notFound' | 'server' | 'unknown';

interface ErrorConfig {
  icon: React.ReactNode;
  title: string;
  description: string;
  suggestion: string;
}

const errorConfigs: Record<ErrorType, ErrorConfig> = {
  network: {
    icon: <WifiOff className="h-16 w-16" />,
    title: 'Connection Lost',
    description: "We couldn't reach the server. Please check your internet connection.",
    suggestion: 'Try reconnecting to your network or refreshing the page.',
  },
  chunk: {
    icon: <FileWarning className="h-16 w-16" />,
    title: 'Page Load Failed',
    description: 'Some resources failed to load. This can happen after a deployment.',
    suggestion: 'Please reload the page.',
  },
  notFound: {
    icon: <FileWarning className="h-16 w-16" />,
    title: 'Page Not Found',
    description: "The page you're looking for doesn't exist or has been moved.",
    suggestion: 'Check the URL or navigate back to the home page.',
  },
  server: {
    icon: <ServerCrash className="h-16 w-16" />,
    title: 'Server Error',
    description: 'Something went wrong on our end. Our team has been notified.',
    suggestion: 'Please try again in a few moments.',
  },
  unknown: {
    icon: <ServerCrash className="h-16 w-16" />,
    title: 'Something Went Wrong',
    description: 'An unexpected error occurred while loading this page.',
    suggestion: 'Try refreshing the page or go back to the home page.',
  },
};

export default function ErrorPage() {
  const error = useRouteError() as Error | Response | { message?: string; statusText?: string; stack?: string };
  const navigate = useNavigate();
  const [showDetails, setShowDetails] = useState(true);
  const [copied, setCopied] = useState(false);

  logger.error('[ErrorPage] Route error occurred', { error });

  const getErrorMessage = (err: typeof error): string | undefined => {
    if (err instanceof Error) return err.message;
    if (err && typeof err === 'object' && 'message' in err) return err.message;
    return undefined;
  };

  const getErrorStack = (err: typeof error): string | undefined => {
    if (err instanceof Error) return err.stack;
    if (err && typeof err === 'object' && 'stack' in err) return err.stack;
    return undefined;
  };

  const getErrorStatus = (err: typeof error): number | undefined => {
    if (err instanceof Response) return err.status;
    if (err && typeof err === 'object' && 'status' in err) return (err as { status: number }).status;
    return undefined;
  };

  const errorMessage = getErrorMessage(error);
  const errorStatus = getErrorStatus(error);

  const getErrorType = (): ErrorType => {
    if (errorStatus === 404) return 'notFound';
    if (errorStatus && errorStatus >= 500) return 'server';
    if (
      errorMessage?.includes('Failed to fetch dynamically imported module') ||
      errorMessage?.includes('Loading chunk') ||
      errorMessage?.includes('Unable to preload CSS')
    ) {
      return 'chunk';
    }
    if (
      errorMessage?.includes('NetworkError') ||
      errorMessage?.includes('Failed to fetch') ||
      errorMessage?.includes('Network request failed')
    ) {
      return 'network';
    }
    return 'unknown';
  };

  const errorType = getErrorType();
  const config = errorConfigs[errorType];
  const errorDetails = getErrorStack(error) || getErrorMessage(error) || JSON.stringify(error, null, 2);

  const copyErrorDetails = async () => {
    await navigator.clipboard.writeText(errorDetails);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (errorType === 'chunk') {
    return (
      <div className="fixed bottom-4 left-1/2 -translate-x-1/2 z-50 w-full max-w-lg mx-auto px-4" id="error-page">
        <div className="rounded-lg border bg-card p-4 shadow-lg space-y-2">
          <div className="flex items-center gap-3">
            <FileWarning className="h-5 w-5 shrink-0 text-muted-foreground" />
            <p className="text-sm text-foreground flex-1">Failed to load page. Try reloading.</p>
            <Button size="sm" onClick={() => window.location.reload()} className="gap-1.5 shrink-0">
              <RefreshCw className="h-3.5 w-3.5" />
              Reload
            </Button>
          </div>
          <pre className="text-xs text-muted-foreground bg-muted/50 rounded p-2 overflow-auto max-h-24 whitespace-pre-wrap break-all">
            {errorMessage}
          </pre>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-background p-6" id="error-page">
      <div className="w-full max-w-md space-y-8 text-center">
        <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-full bg-muted text-muted-foreground">
          {config.icon}
        </div>

        <div className="space-y-3">
          <h1 className="text-3xl font-bold tracking-tight text-foreground">{config.title}</h1>
          <p className="text-base text-muted-foreground">{config.description}</p>
          <p className="text-sm text-muted-foreground/80">{config.suggestion}</p>
        </div>

        <div className="flex flex-col gap-3 pt-4">
          <Button onClick={() => window.location.reload()} size="lg" className="w-full gap-2">
            <RefreshCw className="h-4 w-4" />
            Reload Page
          </Button>
          <Button variant="outline" onClick={() => navigate('/')} size="lg" className="w-full gap-2">
            <Home className="h-4 w-4" />
            Go to Home
          </Button>
        </div>

        {import.meta.env.DEV && error && (
          <div className="pt-4">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex w-full items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
              {showDetails ? 'Hide' : 'Show'} Error Details
            </button>
            {showDetails && (
              <div className="mt-4 rounded-lg border bg-muted/50 p-4 text-left">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Stack Trace</span>
                  <Button variant="ghost" size="sm" onClick={copyErrorDetails} className="h-7 gap-1.5 text-xs">
                    {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                    {copied ? 'Copied' : 'Copy'}
                  </Button>
                </div>
                <pre className="overflow-auto text-xs text-muted-foreground max-h-48 whitespace-pre-wrap wrap-break-word">
                  {errorDetails}
                </pre>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
